<template>
  <div class="kpi-cards-container">
    <div v-for="(card, index) in cards" :key="index" class="kpi-card">
      <div class="kpi-card-header">
        <div class="kpi-icon" :style="{ backgroundColor: card.iconBg }">
          <svg
            class="kpi-icon-svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            v-html="card.iconSvg"
          />
        </div>
        <div
          v-if="card.percentage !== undefined"
          class="kpi-percentage"
          :class="{
            positive: card.percentage > 0,
            negative: card.percentage < 0,
          }"
        >
          <v-icon v-if="card.percentage > 0" icon="mdi-trending-up" size="18" />
          <v-icon
            v-else-if="card.percentage < 0"
            icon="mdi-trending-down"
            size="18"
          />
          <span
            >{{ card.percentage > 0 ? "+" : "" }}{{ card.percentage }}%</span
          >
        </div>
      </div>
      <div class="kpi-body">
        <p class="kpi-value">{{ card.value }}</p>
        <p class="kpi-label">{{ card.label }}</p>
        <p v-if="card.subtitle" class="kpi-subtitle">{{ card.subtitle }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { sfiStore } from "@/services/sfiService";

const ICON_ACTIVE_USERS = `<path d="M12 4.35418C12.7329 3.52375 13.8053 3 15 3C17.2091 3 19 4.79086 19 7C19 9.20914 17.2091 11 15 11C13.8053 11 12.7329 10.4762 12 9.64582M15 21H3V20C3 16.6863 5.68629 14 9 14C12.3137 14 15 16.6863 15 20V21ZM15 21H21V20C21 16.6863 18.3137 14 15 14C13.9071 14 12.8825 14.2922 12 14.8027M13 7C13 9.20914 11.2091 11 9 11C6.79086 11 5 9.20914 5 7C5 4.79086 6.79086 3 9 3C11.2091 3 13 4.79086 13 7Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;

const ICON_EDUCATORS = `<path d="M12 14L21 9L12 4L3 9L12 14ZM12 14L18.1591 10.5783C18.7017 11.9466 19 13.4384 19 14.9999C19 15.7013 18.9398 16.3885 18.8244 17.0569C16.2143 17.3106 13.849 18.4006 12 20.0555C10.151 18.4006 7.78571 17.3106 5.17562 17.0569C5.06017 16.3885 5 15.7012 5 14.9999C5 13.4384 5.29824 11.9466 5.84088 10.5782L12 14ZM8 19.9999V12.5L12 10.2778" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;

const ICON_SESSIONS = `<path d="M4 4V3H3V4H4ZM20 4H21V3H20V4ZM6.29289 11.2929C5.90237 11.6834 5.90237 12.3166 6.29289 12.7071C6.68342 13.0976 7.31658 13.0976 7.70711 12.7071L7 12L6.29289 11.2929ZM10 9L10.7071 8.29289C10.3166 7.90237 9.68342 7.90237 9.29289 8.29289L10 9ZM13 12L12.2929 12.7071C12.6834 13.0976 13.3166 13.0976 13.7071 12.7071L13 12ZM17.7071 8.70711C18.0976 8.31658 18.0976 7.68342 17.7071 7.29289C17.3166 6.90237 16.6834 6.90237 16.2929 7.29289L17 8L17.7071 8.70711ZM7.29289 20.2929C6.90237 20.6834 6.90237 21.3166 7.29289 21.7071C7.68342 22.0976 8.31658 22.0976 8.70711 21.7071L8 21L7.29289 20.2929ZM12 17L12.7071 16.2929C12.3166 15.9024 11.6834 15.9024 11.2929 16.2929L12 17ZM15.2929 21.7071C15.6834 22.0976 16.3166 22.0976 16.7071 21.7071C17.0976 21.3166 17.0976 20.6834 16.7071 20.2929L16 21L15.2929 21.7071ZM3 3C2.44772 3 2 3.44772 2 4C2 4.55228 2.44772 5 3 5V4V3ZM21 5C21.5523 5 22 4.55228 22 4C22 3.44772 21.5523 3 21 3V4V5ZM4 4V5H20V4V3H4V4ZM20 4H19V16H20H21V4H20ZM19 17V16H5V17V18H19V17ZM4 16H5V4H4H3V16H4ZM5 17V16H4H3C3 17.1046 3.89543 18 5 18V17ZM20 16H19V17V18C20.1046 18 21 17.1046 21 16H20ZM7 12L7.70711 12.7071L10.7071 9.70711L10 9L9.29289 8.29289L6.29289 11.2929L7 12ZM10 9L9.29289 9.70711L12.2929 12.7071L13 12L13.7071 11.2929L10.7071 8.29289L10 9ZM13 12L13.7071 12.7071L17.7071 8.70711L17 8L16.2929 7.29289L12.2929 11.2929L13 12ZM8 21L8.70711 21.7071L12.7071 17.7071L12 17L11.2929 16.2929L7.29289 20.2929L8 21ZM12 17L11.2929 17.7071L15.2929 21.7071L16 21L16.7071 20.2929L12.7071 16.2929L12 17ZM3 4V5H21V4V3H3V4Z" fill="white"/>`;

const ICON_TOTAL_ASSIGNMENTS = `<path d="M12 6.25278V19.2528M12 6.25278C10.8321 5.47686 9.24649 5 7.5 5C5.75351 5 4.16789 5.47686 3 6.25278V19.2528C4.16789 18.4769 5.75351 18 7.5 18C9.24649 18 10.8321 18.4769 12 19.2528M12 6.25278C13.1679 5.47686 14.7535 5 16.5 5C18.2465 5 19.8321 5.47686 21 6.25278V19.2528C19.8321 18.4769 18.2465 18 16.5 18C14.7535 18 13.1679 18.4769 12 19.2528" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;

const ICON_MOST_ACTIVE_TOPIC = `<path d="M4.02753 7.81992C3.63937 8.55869 3.35088 9.35802 3.17996 10.2001M7.81992 4.02753C8.55873 3.63936 9.35807 3.35088 10.2001 3.17996M16.1802 4.02753C15.4414 3.63936 14.642 3.35088 13.8 3.17996M19.9723 7.81992C20.3605 8.55869 20.649 9.35802 20.8199 10.2001M19.9723 16.1802C20.3605 15.4414 20.649 14.642 20.8199 13.8M16.1802 19.9723C15.4414 20.3605 14.642 20.649 13.8 20.8199M7.81992 19.9723C8.55873 20.3605 9.35807 20.649 10.2001 20.8199M4.02753 16.1802C3.63937 15.4414 3.35088 14.642 3.17996 13.8M12 14.7V9.3M9.3 12H14.7M5.7 8.4C7.19116 8.4 8.4 7.19116 8.4 5.7C8.4 4.20883 7.19116 3 5.7 3C4.20883 3 3 4.20883 3 5.7C3 7.19116 4.20883 8.4 5.7 8.4ZM18.3 8.4C19.7912 8.4 21 7.19116 21 5.7C21 4.20883 19.7912 3 18.3 3C16.8088 3 15.6 4.20883 15.6 5.7C15.6 7.19116 16.8088 8.4 18.3 8.4ZM18.3 21C19.7912 21 21 19.7912 21 18.3C21 16.8088 19.7912 15.6 18.3 15.6C16.8088 15.6 15.6 16.8088 15.6 18.3C15.6 19.7912 16.8088 21 18.3 21ZM5.7 21C7.19116 21 8.4 19.7912 8.4 18.3C8.4 16.8088 7.19116 15.6 5.7 15.6C4.20883 15.6 3 16.8088 3 18.3C3 19.7912 4.20883 21 5.7 21Z" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>`;

const ICON_AGES = `<path d="M10 6H5C3.89543 6 3 6.89543 3 8V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V8C21 6.89543 20.1046 6 19 6H14M10 6V5C10 3.89543 10.8954 3 12 3C13.1046 3 14 3.89543 14 5V6M10 6C10 7.10457 10.8954 8 12 8C13.1046 8 14 7.10457 14 6M9 14C10.1046 14 11 13.1046 11 12C11 10.8954 10.1046 10 9 10C7.89543 10 7 10.8954 7 12C7 13.1046 7.89543 14 9 14ZM9 14C10.3062 14 11.4174 14.8348 11.8292 16M9 14C7.69378 14 6.58249 14.8348 6.17065 16M15 11H18M15 15H17" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;

const cards = ref([
  {
    label: "Active Users",
    value: "-",
    subtitle: "",
    iconSvg: ICON_ACTIVE_USERS,
    iconBg: "#0478CE",
  },
  {
    label: "Educators",
    value: "-",
    subtitle: "Total registered educators",
    iconSvg: ICON_EDUCATORS,
    iconBg: "#026843",
  },
  {
    label: "Students",
    value: "-",
    subtitle: "Students assigned Starter Packs",
    iconSvg: ICON_ACTIVE_USERS,
    iconBg: "#4545D0",
  },
  {
    label: "Sessions",
    value: "-",
    subtitle: "Total add-on sessions",
    iconSvg: ICON_SESSIONS,
    iconBg: "#00599A",
  },
  {
    label: "Total Assignments",
    value: "-",
    iconSvg: ICON_TOTAL_ASSIGNMENTS,
    iconBg: "#00599A",
  },
  {
    label: "Most Active Topic",
    value: "-",
    iconSvg: ICON_MOST_ACTIVE_TOPIC,
    iconBg: "#4545D0",
  },
  {
    label: "Most Active Age Range",
    value: "-",
    iconSvg: ICON_AGES,
    iconBg: "#026843",
  },
]);

if (sfiStore.data) {
  const keyMetrics = sfiStore.data.keyMetrics || {};

  const setCardValue = (label, value, subtitle) => {
    const card = cards.value.find((c) => c.label === label);
    if (card) {
      card.value = value;
      if (subtitle !== undefined) card.subtitle = subtitle;
    }
  };

  if (keyMetrics.activeUsers !== undefined) {
    const educators = keyMetrics.activeEducators ?? 0;
    const students = keyMetrics.activeStudents ?? 0;
    setCardValue(
      "Active Users",
      keyMetrics.activeUsers,
      `${educators} Educators, ${students} Students`
    );
  }

  if (keyMetrics.totalAssignments !== undefined) {
    setCardValue("Total Assignments", keyMetrics.totalAssignments);
  }

  if (keyMetrics.mostActiveTopic) {
    setCardValue("Most Active Topic", keyMetrics.mostActiveTopic);
  }

  if (keyMetrics.mostActiveRange) {
    setCardValue("Most Active Age Range", `Ages ${keyMetrics.mostActiveRange}`);
  }

  if (keyMetrics.activeEducators !== undefined) {
    setCardValue("Educators", keyMetrics.activeEducators);
  }

  if (keyMetrics.activeStudents !== undefined) {
    setCardValue("Students", keyMetrics.activeStudents);
  }

  if (keyMetrics.totalSessions !== undefined) {
    setCardValue("Sessions", keyMetrics.totalSessions);
  }
}
</script>

<style scoped>
.kpi-cards-container {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  width: 100%;
  padding: 16px 0;
}

@media (max-width: 1024px) {
  .kpi-cards-container {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .kpi-cards-container {
    grid-template-columns: 1fr;
  }
}

.kpi-card {
  background-color: var(--white);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 160px;
  box-shadow:
    0px 2px 3px 0px rgba(0, 0, 0, 0.1),
    0px 6px 10px 0px rgba(0, 0, 0, 0.15);
}

.kpi-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  height: 40px;
  width: 100%;
}

.kpi-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-percentage {
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-body-small);
  line-height: var(--line-height-body-small);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.4px;
}

.kpi-percentage.positive {
  color: var(--primary-green);
}

.kpi-percentage.negative {
  color: var(--error-red);
}

.kpi-body {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.kpi-value {
  font-family: var(--font-family-primary);
  font-size: var(--font-size-headline-large);
  line-height: var(--line-height-headline-large);
  font-weight: var(--font-weight-bold);
  color: var(--primary-blue);
  margin: 0;
  word-break: break-word;
}

.kpi-label {
  font-family: var(--font-family-primary);
  font-size: var(--font-size-title-big);
  line-height: var(--line-height-title-big);
  font-weight: var(--font-weight-regular);
  color: var(--black-body-text);
  letter-spacing: 0.15px;
  margin: 0;
}

.kpi-subtitle {
  font-family: var(--font-family-primary);
  font-size: var(--font-size-body-medium);
  line-height: var(--line-height-body-medium);
  font-weight: var(--font-weight-regular);
  color: var(--neutral-700);
  letter-spacing: 0.25px;
  margin: 0;
}
</style>
